import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["icon", "content", "tooltip", "tlds", "onInfoClick"];
import React, { forwardRef, useMemo, useState } from 'react';
import { Box, Cell, Layout, Text, Tooltip } from '@wix/design-system';
import { Gift, Tag } from '@wix/wix-ui-icons-common/lazy';
import { InfoCircleSmall } from '@wix/wix-ui-icons-common/odeditor';
import s from './voucherBanner.scss';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { ExperimentSpecs } from '../../constants';
import { VoucherTldsModal } from './VoucherTldsModal';
import { useVoucherState } from './voucherState';
const getVoucherConfigs = isMatchCycle => ({
  'premium-with-voucher': {
    icon: /*#__PURE__*/React.createElement(Gift, null),
    content: _ref => {
      let Trans = _ref.Trans;
      return /*#__PURE__*/React.createElement(Trans, {
        i18nKey: isMatchCycle ? 'domain_search_gate.voucher_banner.match_cycle' : 'domain_search_gate.voucher_banner',
        components: [/*#__PURE__*/React.createElement("b", null)]
      });
    },
    tooltip: _ref2 => {
      let translationsUtils = _ref2.translationsUtils,
        tlds = _ref2.tlds;
      return /*#__PURE__*/React.createElement(Box, {
        direction: "vertical",
        gap: "SP3",
        width: "100%",
        padding: "SP3 SP4"
      }, /*#__PURE__*/React.createElement(Text, {
        size: "small",
        weight: "thin",
        light: true
      }, translationsUtils.t('domain_search_gate.voucher_banner.tld_tooltip')), /*#__PURE__*/React.createElement(Layout, {
        justifyItems: "fit-content",
        gap: "0",
        cols: 5
      }, tlds == null ? void 0 : tlds.map(tld => /*#__PURE__*/React.createElement(Cell, {
        key: tld,
        span: 2.4
      }, /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "thin",
        light: true
      }, `.${tld}`)))));
    }
  },
  'premium-without-voucher': {
    icon: /*#__PURE__*/React.createElement(Tag, null),
    content: _ref3 => {
      let t = _ref3.t;
      return t('domain_search_gate.domain_gate.header.non_voucher');
    },
    tooltip: undefined
  },
  'free-plan': {
    icon: /*#__PURE__*/React.createElement(Gift, null),
    content: _ref4 => {
      let t = _ref4.t;
      return t(isMatchCycle ? 'domain_search_gate.domain_gate.header.match_cycle' : 'domain_search_gate.domain_gate.header.one_year_free');
    },
    tooltip: undefined
  }
});
const OPTIMIZATIONS_VOUCHER_CONFIGS = {
  'premium-with-voucher': {
    icon: /*#__PURE__*/React.createElement(Gift, null),
    content: _ref5 => {
      let t = _ref5.t;
      return /*#__PURE__*/React.createElement("b", null, t('domains_search_gate.bm.results_optimizations.initial_vs_renewal.voucher_banner'));
    },
    tooltip: undefined
  },
  'free-plan': {
    icon: /*#__PURE__*/React.createElement(Gift, null),
    content: _ref6 => {
      let tSuite = _ref6.tSuite;
      return tSuite('domains_search_suite.anon_storefront_results.optimizations.initial_vs_renewal.banner');
    },
    tooltip: undefined
  }
};
export const VoucherBanner = _ref7 => {
  var _getPremiumEssentials;
  let _ref7$optimizedVouche = _ref7.optimizedVoucherCopy,
    optimizedVoucherCopy = _ref7$optimizedVouche === void 0 ? false : _ref7$optimizedVouche,
    configOverrides = _ref7.configOverrides;
  const translationsUtils = DomainSearchGateModule.internals.useTranslations();
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    tSuite = _DomainSearchSuiteMod.t;
  const _useVoucherState = useVoucherState(),
    voucherState = _useVoucherState.voucherState,
    isLoading = _useVoucherState.isLoading,
    voucherSupportedTlds = _useVoucherState.voucherSupportedTlds;
  const isMatchCycle = !!((_getPremiumEssentials = getPremiumEssentialsSafe()) != null && (_getPremiumEssentials = _getPremiumEssentials.experiments) != null && _getPremiumEssentials[ExperimentSpecs.MatchDomainCycleToPremiumCycle]);
  const VOUCHER_CONFIGS = useMemo(() => getVoucherConfigs(isMatchCycle), [isMatchCycle]);
  const config = useMemo(() => {
    const base = optimizedVoucherCopy && OPTIMIZATIONS_VOUCHER_CONFIGS[voucherState] || VOUCHER_CONFIGS[voucherState];
    return {
      ...base,
      ...(configOverrides == null ? void 0 : configOverrides[voucherState])
    };
  }, [optimizedVoucherCopy, voucherState, VOUCHER_CONFIGS, configOverrides]);
  const contentUtils = {
    ...translationsUtils,
    tSuite
  };
  const tooltipContent = useMemo(() => config.tooltip == null ? void 0 : config.tooltip({
    translationsUtils,
    tlds: voucherSupportedTlds
  }), [config, translationsUtils, voucherSupportedTlds]);
  if (isLoading) {
    return null;
  }
  return /*#__PURE__*/React.createElement(BaseVoucherBanner, {
    icon: config.icon,
    content: config.content(contentUtils),
    tooltip: tooltipContent,
    tlds: voucherSupportedTlds
  });
};
export const BaseVoucherBanner = /*#__PURE__*/forwardRef((_ref8, ref) => {
  var _getPremiumEssentials2;
  let icon = _ref8.icon,
    content = _ref8.content,
    tooltip = _ref8.tooltip,
    tlds = _ref8.tlds,
    onInfoClick = _ref8.onInfoClick,
    props = _objectWithoutPropertiesLoose(_ref8, _excluded);
  const showModal = !!((_getPremiumEssentials2 = getPremiumEssentialsSafe()) != null && (_getPremiumEssentials2 = _getPremiumEssentials2.experiments) != null && _getPremiumEssentials2[ExperimentSpecs.AvailableTldsModal]);
  const _useState = useState(false),
    isModalOpen = _useState[0],
    setIsModalOpen = _useState[1];
  const infoButtonStyle = {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'inherit'
  };
  const renderInfoIcon = () => {
    if (onInfoClick) {
      return /*#__PURE__*/React.createElement("span", {
        role: "button",
        tabIndex: 0,
        "data-hook": "voucher-banner-info-button",
        onClick: onInfoClick,
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            onInfoClick();
          }
        },
        style: infoButtonStyle
      }, /*#__PURE__*/React.createElement(InfoCircleSmall, {
        className: s.infoIcon
      }));
    }
    if (tooltip && showModal && tlds) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        role: "button",
        tabIndex: 0,
        "data-hook": "voucher-banner-info-button",
        onClick: () => setIsModalOpen(true),
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsModalOpen(true);
          }
        },
        style: infoButtonStyle
      }, /*#__PURE__*/React.createElement(InfoCircleSmall, {
        className: s.infoIcon
      })), /*#__PURE__*/React.createElement(VoucherTldsModal, {
        tlds: tlds,
        isOpen: isModalOpen,
        onRequestClose: () => setIsModalOpen(false)
      }));
    }
    if (tooltip) {
      return /*#__PURE__*/React.createElement(Tooltip, {
        content: tooltip,
        maxWidth: "300px"
      }, /*#__PURE__*/React.createElement(Box, {
        verticalAlign: "middle"
      }, /*#__PURE__*/React.createElement(InfoCircleSmall, {
        className: s.infoIcon
      })));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    align: "center",
    padding: "SP2 SP4",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#DFF994",
    color: "#006F3A",
    verticalAlign: "middle",
    boxSizing: "border-box",
    gap: "2px"
  }, props), icon, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    className: s.voucherBannerText,
    weight: "normal"
  }, content), renderInfoIcon());
});
//# sourceMappingURL=VoucherBanner.js.map